import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';

import { Popover } from '../../../../../../global/popover/popover';
import { MeetingAttendeeJoinMethod, MeetingAttendeeStatus } from '../../../../../../../api/attendees.dao';
import ChevronSVG from '../../../../../../../images/svg/icons8-expand-arrow';
import { OERadioOptions } from '../../../../../../global/oe-radio-options/oe-radio-options';
import { IJoinMethodOption } from '../_attendee';
import VideoSVG from '../../../../../../../images/svg/icons8-video-calls';
import AudioSVG from '../../../../../../../images/svg/icons8-audio';
import PersonSVG from '../../../../../../../images/svg/icons8-person';

import './joined-button.scss';
import Text from '../../../../../../global/text/text';

interface IProps {
    joinMethod?: MeetingAttendeeJoinMethod;
    joinMethodOptions: IJoinMethodOption[];
    onChangeJoinMethod: (value: string) => void;
    intl: IntlShape;
    withTableView?: boolean;
    isMeetingUnderway: boolean;
    status: MeetingAttendeeStatus;
}

const JoinedButton = injectIntl((props: IProps) => {
    const { joinMethod, joinMethodOptions, onChangeJoinMethod, intl, withTableView, isMeetingUnderway, status } = props;
    const buttonRef = useRef(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const shouldHighlight = isMeetingUnderway && !joinMethod &&
        (status === MeetingAttendeeStatus.CONFIRMED || status === MeetingAttendeeStatus.JOINED || status === MeetingAttendeeStatus.WALK_IN);

    const getJoinMethod = (): JSX.Element => {
        if (!joinMethod) {
            return <Text
                        className={classnames('attendee-button__label', {
                            'attendee-button__label--placeholder': !joinMethod
                        })}
                        message={withTableView ? intl.formatMessage({
                            id: 'oe.control.av',
                            defaultMessage: 'Report AV{suffix}',
                            description: 'Cell text',
                        },
                        {
                            suffix: '...'
                        }) : intl.formatMessage({
                            id: 'oe.control.av',
                            defaultMessage: 'AV',
                            description: 'Cell text',
                        })}
                        withEllipsis
                    />;
        } else if (joinMethod === MeetingAttendeeJoinMethod.AUDIO) {
            return <>
                <AudioSVG />
                { withTableView && <Text
                    className={classnames('attendee-button__label')}
                    message={intl.formatMessage({
                        id: 'oe.common.audio',
                        defaultMessage: 'Audio',
                        description: 'Cell text',
                    })}
                    withEllipsis
                /> }
            </>;
        } else if (joinMethod === MeetingAttendeeJoinMethod.VIDEO) {
            return <>
                    <VideoSVG />
                    { withTableView && <Text
                        className={classnames('attendee-button__label')}
                        message={intl.formatMessage({
                            id: 'oe.common.video',
                            defaultMessage: 'Video',
                            description: 'Cell text'
                        })}
                        withEllipsis
                    />}
                </>;
        } else if (joinMethod === MeetingAttendeeJoinMethod.IN_PERSON) {
            return <>
                    <PersonSVG />
                    { withTableView && <Text
                        className={classnames('attendee-button__label')}
                        message={intl.formatMessage({
                            id: 'oe.common.inPerson',
                            defaultMessage: 'In-Person',
                            description: 'Cell text'
                        })}
                        withEllipsis
                    />}
                </>;
        }
    };

    const handleChangeJoinStatus = (value: string) => {
        onChangeJoinMethod(value);
        setModalOpen(false);
    };

    return (
        <div
            className={classnames('attendee-button', {
                'attendee-button--active': isModalOpen,
                'attendee-button--collapse': !withTableView
            })}
            ref={buttonRef}
        >
            <div className={classnames('attendee-button__wrapper', {'attendee-button__wrapper--highlighted': shouldHighlight})}
                 onClick={() => setModalOpen(prevState => !prevState)}>
                {getJoinMethod()}
                <div className='attendee-button__chevron' >
                    <ChevronSVG />
                </div>
            </div>
            <Popover
                padding={0}
                isOpen={isModalOpen}
                buttonRef={buttonRef}
                customWidth={withTableView ? buttonRef.current?.getBoundingClientRect().width : 100}
                customLeft={0}
                onClickOutside={() => setModalOpen(false)}
                onClose={() => setModalOpen(false)}
            >
                <OERadioOptions
                    value={joinMethod}
                    onOptionToggle={handleChangeJoinStatus}
                    options={joinMethodOptions}
                    shouldHideRadioButton
                    isIconFirst
                />
            </Popover>
        </div>
    );
});

export default JoinedButton;
